<template>
  <div class="sales-category">
    <div class="module-header">
      <h3 class="module-title">销售大类占比</h3>
      <div class="time-selector">
        <button 
          v-for="option in timeOptions" 
          :key="option.value"
          :class="['time-btn', { active: salesCategory.period === option.value }]"
          @click="changePeriod(option.value)"
        >
          {{ option.label }}
        </button>
      </div>
    </div>
    <div class="cards">
      <div class="sale-card" v-for="(card, idx) in topThree" :key="card.categoryId || idx">
        <div class="ring-box">
          <div
            class="ring-bg"
            :style="{ backgroundImage: 'url(' + ringBgList[idx % ringBgList.length] + ')' }"
          ></div>
          <div ref="ring" class="ring"></div>
          <div class="ring-center">
            <div class="percent">{{ Math.round(card.percentage || 0) }}%</div>
            <div class="amount">{{ formatWan(card.salesAmount) }}万</div>
          </div>
        </div>
        <div class="label">{{ card.categoryName }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import * as echarts from 'echarts'
import ring01 from '@/assets/images/ring_color_01.png'
import ring02 from '@/assets/images/ring_color_02.png'
import ring03 from '@/assets/images/ring_color_03.png'

export default {
  name: 'SalesCategory',
  data() {
    return {
      ringCharts: [],
      timeOptions: [
        { label: '今日', value: 'today' },
        { label: '本周', value: 'week' },
        { label: '本月', value: 'month' }
      ],
      ringBgList: [ring01, ring02, ring03],
      lastDataSnapshot: null, // 用于数据变化检测
      isInitialized: false // 标记是否已初始化
    }
  },
  computed: {
    ...mapState('sales', ['salesCategory']),
    topThree() {
      const list = Array.isArray(this.salesCategory?.categories)
        ? this.salesCategory.categories.slice(0)
        : []
      return list
        .sort((a, b) => a.categoryId - b.categoryId)
        .slice(0, 3)
    }
  },
  mounted() {
    // 初次渲染由全局数据加载完成后通过 watcher 触发图表绘制
    window.addEventListener('resize', this.resizeAll)
    this.$nextTick(() => {
      const hasData = Array.isArray(this.salesCategory?.categories) && this.salesCategory.categories.length > 0
      if (hasData) {
        this.updateDataSnapshot(this.topThree)
        this.renderAll()
      }
    })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeAll)
    this.disposeAll()
  },
  watch: {
    'salesCategory.categories': {
      handler(newCategories) {
        // 只有在数据真正发生变化时才重新渲染
        if (this.hasDataChanged(this.topThree)) {
          this.updateDataSnapshot(this.topThree)
          this.$nextTick(() => this.renderAll())
        }
      },
      deep: true
    }
  },
  methods: {
    ...mapActions('sales', ['fetchSalesCategory']),
    
    // 智能渲染：只在必要时重新创建图表
    renderAll() {
      const items = this.topThree
      const rings = this.$refs.ring || []
      
      // 如果没有数据，清理所有图表
      if (!items.length) {
        this.disposeAll()
        return
      }
      
      // 确保图表数组长度匹配
      this.ensureChartsLength(items.length)
      
      // 渲染每个环形图
      items.forEach((item, idx) => {
        if (rings[idx]) {
          this.renderOrUpdateRing(rings[idx], idx, item)
        }
      })
      
      this.isInitialized = true
    },
    
    // 确保图表数组长度正确
    ensureChartsLength(targetLength) {
      // 如果图表数量超过需要的数量，销毁多余的
      while (this.ringCharts.length > targetLength) {
        const chart = this.ringCharts.pop()
        if (chart && !chart.isDisposed()) {
          chart.dispose()
        }
      }
      
      // 如果图表数量不足，扩展数组
      while (this.ringCharts.length < targetLength) {
        this.ringCharts.push(null)
      }
    },
    
    // 渲染或更新单个环形图
    renderOrUpdateRing(el, idx, item) {
      if (!el) return
      
      const percent = Math.round(item?.percentage || 0)
      const colorMap = ['#2391FF', '#FF745A', '#FFC229']
      const trackColor = 'rgba(0,0,0,0)'
      
      // 检查是否需要重新创建图表
      let chart = this.ringCharts[idx]
      const needsRecreate = !chart || chart.isDisposed() || !this.isInitialized
      
      if (needsRecreate) {
        // 销毁旧图表（如果存在）
        if (chart && !chart.isDisposed()) {
          chart.dispose()
        }
        
        // 创建新图表
        chart = echarts.init(el)
        this.ringCharts[idx] = chart
        
        // 设置完整配置
        const option = {
          tooltip: { show: false },
          series: [
            {
              name: 'progress',
              type: 'pie',
              radius: ['75%', '98%'],
              center: ['50%', '50%'],
              startAngle: 90,
              clockwise: true,
              avoidLabelOverlap: true,
              label: { show: false },
              labelLine: { show: false },
              silent: true,
              emphasis: { disabled: true },
              data: [
                { value: percent, itemStyle: { color: colorMap[idx % colorMap.length] } },
                { value: 100 - percent, itemStyle: { color: trackColor } }
              ]
            }
          ]
        }
        chart.setOption(option)
      } else {
        // 只更新数据部分
        const newData = [
          { value: percent, itemStyle: { color: colorMap[idx % colorMap.length] } },
          { value: 100 - percent, itemStyle: { color: trackColor } }
        ]
        
        chart.setOption({
          series: [{
            data: newData
          }]
        }, false) // false 表示不合并配置，只更新数据
      }
    },
    
    // 检查数据是否发生实质性变化
    hasDataChanged(newData) {
      if (!this.lastDataSnapshot) return true
      
      if (newData.length !== this.lastDataSnapshot.length) return true
      
      return newData.some((item, idx) => {
        const oldItem = this.lastDataSnapshot[idx]
        return !oldItem || 
               item.categoryId !== oldItem.categoryId ||
               Math.abs((item.percentage || 0) - (oldItem.percentage || 0)) > 0.01 ||
               Math.abs((item.salesAmount || 0) - (oldItem.salesAmount || 0)) > 0.01
      })
    },
    
    // 更新数据快照
    updateDataSnapshot(data) {
      this.lastDataSnapshot = data.map(item => ({
        categoryId: item.categoryId,
        percentage: item.percentage || 0,
        salesAmount: item.salesAmount || 0,
        categoryName: item.categoryName
      }))
    },
    disposeAll() {
      this.ringCharts.forEach((chart) => {
        if (chart && !chart.isDisposed()) chart.dispose()
      })
      this.ringCharts = []
      this.isInitialized = false
      this.lastDataSnapshot = null
    },
    resizeAll() {
      this.ringCharts.forEach((chart) => {
        if (chart && !chart.isDisposed()) {
          chart.resize()
        }
      })
    },
    // 保留原有的renderRing方法作为备用（已被renderOrUpdateRing替代）
    renderRing(el, idx, item) {
      if (!el) return
      const chart = echarts.init(el)
      this.ringCharts[idx] = chart
      const percent = Math.round(item?.percentage || 0)
      const colorMap = ['#2391FF', '#FF745A', '#FFC229']
      const trackColor = 'rgba(0,0,0,0)'
      const option = {
        tooltip: { show: false },
        series: [
          {
            name: 'progress',
            type: 'pie',
            radius: ['75%', '98%'],
            center: ['50%', '50%'],
            startAngle: 90,
            clockwise: true,
            avoidLabelOverlap: true,
            label: { show: false },
            labelLine: { show: false },
            silent: true,
            emphasis: { disabled: true },
            data: [
              { value: percent, itemStyle: { color: colorMap[idx % colorMap.length] } },
              { value: 100 - percent, itemStyle: { color: trackColor } }
            ]
          }
        ]
      }
      chart.setOption(option)
    },
    async changePeriod(period) {
      if (period !== this.salesCategory.period) {
        await this.fetchSalesCategory(period)
        // 数据变化会通过watcher自动触发渲染，这里不需要手动调用renderAll
      }
    },
    formatWan(value) {
      const num = Number(value) || 0
      return (num / 10000).toFixed(2)
    }
  }
}
</script>

<style lang="scss" scoped>
.sales-category {
  height: 100%;
  display: flex;
  flex-direction: column;

  .cards {
    flex: 1;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    align-items: center;
    justify-items: center;
    padding: 10px 0 30px;
  }

  .sale-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .ring-box {
    position: relative;
    width: 87px;
    height: 87px;
  }

  .ring {
    width: 100%;
    height: 100%;
  }

  // 底部静态空心环背景
  .ring-bg {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    height: 100%;
    background-repeat: no-repeat;
    background-position: center center;
    background-size: 99% auto;
    pointer-events: none; // 保持静态不交互
  }

  .ring-center {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
    pointer-events: none;
  }

  .percent {
    color: #FFFEFE;
    font-weight: 700;
    font-size: 18px;
    line-height: 1;
    margin-bottom: 5px;
  }

  .amount {
    color: #ffffff;
    opacity: 0.9;
    font-size: 12px;
    white-space: nowrap;
  }

  .label {
    margin-top: 14px;
    line-height: 1;
    color: #FFFEFE;
    font-size: 16px;
    text-align: center;
  }
}

</style>